export interface GameLibraryArtworkDefinition {
  atlas: string
  index: number
  offsetX?: number
  offsetY?: number
}

const CATEGORY_ATLAS = "/images/game-library/category-icons-v1.webp"
const GAME_ATLAS_A = "/images/game-library/game-icons-a-v1.webp"
const GAME_ATLAS_B = "/images/game-library/game-icons-b-v1.webp"
const GAME_ATLAS_C = "/images/game-library/game-icons-c-v1.webp"
const GAME_ATLAS_D = "/images/game-library/game-icons-d-v1.webp"
const GAME_ATLAS_E = "/images/game-library/game-icons-e-v1.webp"
const GAME_ATLAS_F = "/images/game-library/game-icons-f-v1.webp"

const EXISTING_ATLAS_OFFSETS: Record<
  string,
  ReadonlyArray<readonly [number, number]>
> = {
  [CATEGORY_ATLAS]: [
    [-7.62, -7.81],
    [-2.15, -9.77],
    [4.49, -8.01],
    [9.38, -10.16],
    [-7.62, -1.95],
    [2.54, -0.78],
    [-4.3, -0.59],
    [9.96, -4.88],
    [-4.69, 4.88],
    [-0.2, 2.34],
    [-3.13, 1.76],
    [14.65, 1.76],
    [-4.49, 9.57],
    [5.27, 8.59],
    [9.57, 9.38],
    [5.47, 5.47],
  ],
  [GAME_ATLAS_A]: [
    [-6.05, -13.67],
    [-3.71, -11.33],
    [-6.45, -7.23],
    [8.79, -17.38],
    [-6.05, -14.26],
    [-4.1, -3.32],
    [1.76, -6.64],
    [6.05, -7.81],
    [-1.56, 4.3],
    [5.08, -3.91],
    [3.52, 1.37],
    [11.72, 0.39],
    [-4.49, 11.13],
    [-4.1, 12.3],
    [2.54, 11.33],
    [8.79, 10.35],
  ],
  [GAME_ATLAS_B]: [
    [-4.3, -8.98],
    [-3.52, -10.74],
    [1.37, -9.96],
    [5.27, -12.89],
    [-3.13, -6.25],
    [-0.98, -8.2],
    [5.66, -2.34],
    [6.64, -5.86],
    [-4.49, 0.59],
    [-0.98, 1.37],
    [4.1, 1.76],
    [6.64, 0.78],
    [-4.3, 11.52],
    [-0.39, 12.5],
    [6.64, 10.16],
    [7.23, 8.98],
  ],
  [GAME_ATLAS_C]: [
    [-3.32, -15.04],
    [-2.15, -8.98],
    [5.66, -14.45],
    [5.08, -11.72],
    [-4.49, -13.28],
    [1.17, -7.23],
    [1.56, -14.06],
    [5.08, -12.11],
    [-3.32, -8.79],
    [0.78, 4.1],
    [8.98, -7.23],
    [7.03, 4.49],
    [0.2, -1.56],
    [2.15, -1.37],
    [5.66, -0.39],
    [10.16, -1.17],
  ],
  [GAME_ATLAS_D]: [
    [-7.42, -3.13],
    [-2.34, -6.05],
    [3.13, -5.86],
    [6.84, -5.27],
    [-3.13, -2.34],
    [-0.59, -2.15],
    [-3.71, -4.49],
    [8.01, -4.3],
    [0.59, 0.98],
    [2.54, 0.98],
    [-3.71, 2.34],
    [8.2, 1.17],
    [-1.95, 9.57],
    [1.56, 8.79],
    [-4.1, 10.35],
    [11.52, 8.01],
  ],
  [GAME_ATLAS_E]: [
    [-5.27, -10.16],
    [-1.95, -10.74],
    [4.3, -13.87],
    [8.2, -13.87],
    [-2.34, -7.62],
    [-0.2, 1.95],
    [-2.34, -7.42],
    [8.59, -8.2],
    [-3.52, 2.73],
    [0.2, -0.78],
    [4.1, -1.56],
    [7.81, -0.78],
    [-2.73, 8.01],
    [0.2, 9.18],
    [2.73, 10.74],
    [8.01, 10.55],
  ],
  [GAME_ATLAS_F]: [
    [-4.49, -14.65],
    [1.37, -14.65],
    [-0.39, -15.82],
    [6.05, -15.63],
    [-7.62, 0],
  ],
}

function artwork(
  atlas: string,
  index: number,
): GameLibraryArtworkDefinition {
  const [offsetX = 0, offsetY = 0] =
    EXISTING_ATLAS_OFFSETS[atlas]?.[index] ?? []

  return { atlas, index, offsetX, offsetY }
}

export const CATEGORY_LIBRARY_ARTWORK: Record<
  string,
  GameLibraryArtworkDefinition
> = {
  animals: artwork(CATEGORY_ATLAS, 0),
  anime: artwork(CATEGORY_ATLAS, 1),
  architecture: artwork(CATEGORY_ATLAS, 2),
  books: artwork(CATEGORY_ATLAS, 3),
  cars: artwork(CATEGORY_ATLAS, 4),
  "dutch-houses": artwork(CATEGORY_ATLAS, 5),
  entertainment: artwork(CATEGORY_ATLAS, 6),
  gaming: artwork(CATEGORY_ATLAS, 7),
  geography: artwork(CATEGORY_ATLAS, 8),
  movies: artwork(CATEGORY_ATLAS, 9),
  music: artwork(CATEGORY_ATLAS, 10),
  "real-estate": artwork(CATEGORY_ATLAS, 11),
  "social-media": artwork(CATEGORY_ATLAS, 12),
  sports: artwork(CATEGORY_ATLAS, 13),
  technology: artwork(CATEGORY_ATLAS, 14),
  travel: artwork(CATEGORY_ATLAS, 15),
}

export const GAME_LIBRARY_ARTWORK: Record<
  string,
  GameLibraryArtworkDefinition
> = {
  "animals/animal-weight": artwork(GAME_ATLAS_A, 0),
  "anime/anime-episode-count": artwork(GAME_ATLAS_A, 1),
  "architecture/skyscraper-heights": artwork(GAME_ATLAS_A, 2),
  "books/book-page-count": artwork(GAME_ATLAS_A, 3),
  "cars/car-prices": artwork(GAME_ATLAS_A, 4),
  "real-estate/dutch-house-prices": artwork(GAME_ATLAS_A, 5),
  "entertainment/celebrity-ages": artwork(GAME_ATLAS_A, 6),
  "entertainment/actors-net-worth": artwork(GAME_ATLAS_A, 7),
  "gaming/pokemon-height": artwork(GAME_ATLAS_A, 8),
  "gaming/pokemon-weight": artwork(GAME_ATLAS_A, 9),
  "gaming/pokemon-card-prices": artwork(GAME_ATLAS_A, 10),
  "gaming/steam-peak-players": artwork(GAME_ATLAS_A, 11),
  "gaming/video-game-release-year": artwork(GAME_ATLAS_A, 12),
  "geography/city-population": artwork(GAME_ATLAS_A, 13),
  "geography/country-population": artwork(GAME_ATLAS_A, 14),
  "geography/country-size": artwork(GAME_ATLAS_A, 15),

  "geography/mountain-heights": artwork(GAME_ATLAS_B, 0),
  "geography/us-state-population": artwork(GAME_ATLAS_B, 1),
  "movies/box-office": artwork(GAME_ATLAS_B, 2),
  "movies/imdb-ratings": artwork(GAME_ATLAS_B, 3),
  "movies/movie-budgets": artwork(GAME_ATLAS_B, 4),
  "movies/movie-runtime": artwork(GAME_ATLAS_B, 5),
  "music/spotify-streams": artwork(GAME_ATLAS_B, 6),
  "real-estate/house-prices": artwork(GAME_ATLAS_B, 7),
  "social-media/youtube-subscribers": artwork(GAME_ATLAS_B, 8),
  "sports/football-market-value": artwork(GAME_ATLAS_B, 9),
  "sports/nba-salaries": artwork(GAME_ATLAS_B, 10),
  "sports/nfl-salaries": artwork(GAME_ATLAS_B, 11),
  "sports/stadium-capacity": artwork(GAME_ATLAS_B, 12),
  "technology/google-searches": artwork(GAME_ATLAS_B, 13),
  "technology/internet-speeds": artwork(GAME_ATLAS_B, 14),
  "travel/airport-passenger-traffic": artwork(GAME_ATLAS_B, 15),

  "animals/animal-gestation-period": artwork(GAME_ATLAS_C, 0),
  "animals/animal-litter-size": artwork(GAME_ATLAS_C, 1),
  "animals/animal-wingspan": artwork(GAME_ATLAS_C, 2),
  "animals/egg-incubation-period": artwork(GAME_ATLAS_C, 3),
  "anime/anime-favorites": artwork(GAME_ATLAS_C, 4),
  "anime/anime-popularity": artwork(GAME_ATLAS_C, 5),
  "anime/anime-ratings": artwork(GAME_ATLAS_C, 6),
  "anime/anime-release-year": artwork(GAME_ATLAS_C, 7),
  "architecture/bridge-length": artwork(GAME_ATLAS_C, 8),
  "architecture/building-completion-year": artwork(GAME_ATLAS_C, 9),
  "architecture/building-floor-count": artwork(GAME_ATLAS_C, 10),
  "architecture/tunnel-length": artwork(GAME_ATLAS_C, 11),
  "books/book-edition-count": artwork(GAME_ATLAS_C, 12),
  "books/book-publication-year": artwork(GAME_ATLAS_C, 13),
  "books/book-ratings": artwork(GAME_ATLAS_C, 14),
  "books/book-reading-popularity": artwork(GAME_ATLAS_C, 15),

  "cars/car-city-efficiency": artwork(GAME_ATLAS_D, 0),
  "cars/car-engine-size": artwork(GAME_ATLAS_D, 1),
  "cars/car-fuel-economy": artwork(GAME_ATLAS_D, 2),
  "cars/car-horsepower": artwork(GAME_ATLAS_D, 3),
  "entertainment/celebrity-heights": artwork(GAME_ATLAS_D, 4),
  "entertainment/tv-show-episode-count": artwork(GAME_ATLAS_D, 5),
  "entertainment/tv-show-ratings": artwork(GAME_ATLAS_D, 6),
  "gaming/pokemon-base-stats": artwork(GAME_ATLAS_D, 7),
  "geography/country-co2-emissions": artwork(GAME_ATLAS_D, 8),
  "geography/country-gdp": artwork(GAME_ATLAS_D, 9),
  "geography/country-life-expectancy": artwork(GAME_ATLAS_D, 10),
  "geography/gdp-per-capita": artwork(GAME_ATLAS_D, 11),
  "geography/island-area": artwork(GAME_ATLAS_D, 12),
  "geography/lake-surface-area": artwork(GAME_ATLAS_D, 13),
  "geography/country-population-density": artwork(GAME_ATLAS_D, 14),
  "geography/river-length": artwork(GAME_ATLAS_D, 15),

  "geography/volcano-elevation": artwork(GAME_ATLAS_E, 0),
  "movies/movie-rating-votes": artwork(GAME_ATLAS_E, 1),
  "music/album-length": artwork(GAME_ATLAS_E, 2),
  "music/album-release-year": artwork(GAME_ATLAS_E, 3),
  "music/album-track-count": artwork(GAME_ATLAS_E, 4),
  "music/album-fans": artwork(GAME_ATLAS_E, 5),
  "real-estate/us-house-bedrooms": artwork(GAME_ATLAS_E, 6),
  "real-estate/us-house-size": artwork(GAME_ATLAS_E, 7),
  "real-estate/us-price-per-square-foot": artwork(GAME_ATLAS_E, 8),
  "social-media/youtube-average-views": artwork(GAME_ATLAS_E, 9),
  "social-media/youtube-channel-views": artwork(GAME_ATLAS_E, 10),
  "social-media/youtube-total-views": artwork(GAME_ATLAS_E, 11),
  "social-media/youtube-upload-count": artwork(GAME_ATLAS_E, 12),
  "sports/olympic-medal-count": artwork(GAME_ATLAS_E, 13),
  "technology/internet-users-by-country": artwork(GAME_ATLAS_E, 14),
  "technology/mobile-subscriptions-by-country": artwork(GAME_ATLAS_E, 15),

  "technology/secure-internet-servers": artwork(GAME_ATLAS_F, 0),
  "travel/air-passengers-by-country": artwork(GAME_ATLAS_F, 1),
  "travel/cruise-ship-capacity": artwork(GAME_ATLAS_F, 2),
  "travel/international-tourist-arrivals": artwork(GAME_ATLAS_F, 3),
  "travel/ship-length": artwork(GAME_ATLAS_F, 4),
}

export function getGameLibraryArtwork(
  categorySlug: string,
  gameSlug?: string,
): GameLibraryArtworkDefinition | undefined {
  return gameSlug
    ? GAME_LIBRARY_ARTWORK[`${categorySlug}/${gameSlug}`]
    : CATEGORY_LIBRARY_ARTWORK[categorySlug]
}

